import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  Copy,
  Facebook,
  HeartPulse,
  Images,
  Instagram,
  LayoutGrid,
  PenLine,
  Link2,
  ListTodo,
  MessageCircle,
  MessageSquare,
  Palette,
  Pause,
  Play,
  RefreshCw,
  Settings,
  ShoppingBag,
  Sparkles,
  StickyNote,
  Trash2,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { api, CarouselItem, type CarouselInspirationBorrow, type CarouselLayoutQa } from "@/lib/api";
import { TONE_CLASS, type SettingsTone } from "@/lib/carouselSettingsMeta";
import { copyText, packFromItem } from "@/lib/carouselGenerationPack";
import {
  recommendCta,
  SOURCE_EMPTY,
  STATUS_TIPS,
  STUDIO_TAB_META,
  STUDIO_TIPS,
  houseCanonState,
  studioTabFromHash,
  studioTabToHash,
  type AbVariable,
  type BgMode,
  type CloseMode,
  type CtaIntent,
  type StudioTab,
} from "@/lib/carouselStudioMeta";
import { type ConceptsTraceOutgoing } from "@/lib/carouselGenerationPack";
import PageHeader from "@/components/PageHeader";
import AiJobModal, { type AiJobKind, type AiJobParam } from "@/components/AiJobModal";
import { AiActionButton, BriefPreview, CopyOutcomePanel, CtaIo, StudioProcessChain, StudioSplit, StudioTabsList } from "@/components/studioTabs";
import { StudioSection } from "@/components/studioVoice";
import { StudioModalBody, StudioModalFooter, StudioModalFrame, StudioModalHeader } from "@/components/StudioModalFrame";
import { DesignAnalysisCard, DesignLayoutNoteCard, HouseCanonBar, StudioOptionCard } from "@/components/DesignReviewPanel";
import CarouselReelPanel from "@/components/CarouselReelPanel";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { emitInlineMarkdown, type TextMark } from "@/lib/carouselTextMarks";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { InfoTip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";


const STATUS_LABEL: Record<string, string> = {
  idea: "pomysły", draft: "szkic", approved: "zatwierdzona", scheduled: "zaplanowana",
  publishing: "publikacja", published: "opublikowana", failed: "błąd",
};

function pct(n?: number) {
  if (n == null || Number.isNaN(n)) return "";
  return `${(n * 100).toFixed(1)}%`;
}

export function ideaDescription(idea: { concept?: string; scene?: string; structure?: string }): string {
  return (idea.concept || idea.scene || idea.structure || "").trim();
}

export function firstSlideField(slide: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const val = slide[key];
    if (val != null && String(val).trim()) return String(val);
  }
  return "";
}

export function isHeadlineSlide(slide: Record<string, unknown>): boolean {
  const type = String(slide.type || "").toLowerCase();
  return type === "hook" || type === "content" || type === "close" || type === "action" || !type;
}

export function slideHasCopy(slide: Record<string, unknown>): boolean {
  return Boolean(flattenSlide(slide).trim());
}

export function previewMarks(slide: Record<string, unknown>, field: "text" | "sub", text: string): TextMark[] {
  const key = field === "text" ? "text_marks" : "sub_marks";
  const marks = (slide[key] as TextMark[] | undefined) || [];
  if (marks.length) return marks;
  const emphasis = String(slide.emphasis || "");
  if (field === "sub" && emphasis && text.includes(emphasis)) {
    const start = text.indexOf(emphasis);
    return [{ start, end: start + emphasis.length, highlight: "yellow" }];
  }
  return [];
}

export function CopyMarkdownCheat() {
  const lines = STUDIO_TIPS.slideMarkdown.split("\n").filter(Boolean);
  return (
    <aside className="rounded-md border border-border bg-muted/40 p-2 text-[11px] leading-snug text-muted-foreground">
      <p className="mb-1 font-medium text-foreground">Markdown na kartach</p>
      <ul className="space-y-1">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </aside>
  );
}

export function CopyMarkdownField({
  label,
  text,
  marks,
  bold,
  disabled,
  minHeightClass = "min-h-[72px]",
  fill,
  onDraft,
  onCommit,
}: {
  label: string;
  text: string;
  marks?: TextMark[];
  bold?: boolean;
  disabled?: boolean;
  minHeightClass?: string;
  fill?: boolean;
  onDraft: (next: string) => void;
  onCommit: (next: string) => void;
}) {
  const emitted = emitInlineMarkdown(text, marks);
  const [value, setValue] = useState(emitted);
  const dirty = useRef(false);
  useEffect(() => {
    if (!dirty.current) setValue(emitted);
  }, [emitted]);
  return (
    <div className={cn(fill && "flex min-h-0 flex-1 flex-col")}>
      <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <textarea
        className={cn(
          "w-full rounded-md border border-input bg-background p-2 text-sm leading-relaxed",
          minHeightClass,
          bold && "font-semibold",
          fill && "h-full min-h-0 flex-1",
        )}
        value={value}
        disabled={disabled}
        spellCheck
        onChange={(e) => {
          dirty.current = true;
          setValue(e.target.value);
          onDraft(e.target.value);
        }}
        onBlur={(e) => {
          if (!dirty.current) return;
          dirty.current = false;
          const next = e.target.value;
          if (next === emitted) return;
          onCommit(next);
        }}
      />
    </div>
  );
}

export function flattenSlide(slide: Record<string, unknown>): string {
  const type = String(slide.type || "").toLowerCase();
  const join = (...parts: unknown[]) => parts.filter((p) => p != null && String(p).trim()).map(String).join("\n");
  if (type === "hook") {
    return join(firstSlideField(slide, "text", "heading"), firstSlideField(slide, "sub", "body"));
  }
  if (type === "vs") {
    return join(firstSlideField(slide, "left", "text", "heading"), firstSlideField(slide, "right", "body", "sub"), slide.footer);
  }
  if (type === "steps") {
    const items = Array.isArray(slide.items) ? slide.items.map(String) : [];
    return join(slide.heading, ...items);
  }
  if (type === "diagram") {
    const nodes = Array.isArray(slide.nodes)
      ? (slide.nodes as { label?: string; text?: string }[]).map((n) => `${n.label || ""}: ${n.text || ""}`)
      : [];
    return join(slide.heading, ...nodes);
  }
  if (type === "swap") {
    return join(firstSlideField(slide, "bad", "left", "text"), firstSlideField(slide, "good", "right", "body"));
  }
  if (type === "action") {
    return join(slide.heading, slide.body);
  }
  if (type === "close") {
    return join(firstSlideField(slide, "heading", "text", "body"), firstSlideField(slide, "source", "footer"));
  }
  return join(firstSlideField(slide, "heading", "text"), firstSlideField(slide, "body", "sub", "footer"));
}

export function FinalSlidesModal({
  item,
  mode,
  baking,
  bakeError,
  confirmPending,
  confirmError,
  qaBlocked,
  qaFailed,
  qaOverride,
  qaLabel,
  fixPending,
  slotLabel,
  slotReason,
  when,
  onWhenChange,
  onQaOverride,
  onFixLayout,
  onClose,
  onConfirm,
}: {
  item: CarouselItem;
  mode: "approve" | "publish";
  baking: boolean;
  bakeError: string;
  confirmPending: boolean;
  confirmError: string;
  qaBlocked: boolean;
  qaFailed: boolean;
  qaOverride: boolean;
  qaLabel: string;
  fixPending: boolean;
  slotLabel?: string;
  slotReason?: string;
  when?: string;
  onWhenChange?: (next: string) => void;
  onQaOverride: (next: boolean) => void;
  onFixLayout: () => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const slides = item.slides || [];
  const bust = item.updated_at || item.render_done || "";
  const busy = baking || confirmPending || fixPending;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-auto rounded-lg border border-border bg-card p-5 text-card-foreground shadow-xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-medium">
              {mode === "publish" ? "Sprawdź finalne slajdy przed wysyłką" : "Finalne slajdy do zatwierdzenia"}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {baking
                ? "Składam PNG z aktualnego układu. To te same pliki, które pójdą na IG/FB."
                : "To złożone PNG, nie podgląd z edytora. Jak coś ucięte, wróć do składu."}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={onClose} disabled={confirmPending}>
            Wróć
          </Button>
        </div>
        {qaLabel ? (
          <div
            className={cn(
              "mb-3 rounded-md border px-3 py-2 text-sm",
              qaFailed ? "border-red-300 bg-red-50 text-red-800" : "border-emerald-200 bg-emerald-50 text-emerald-800",
            )}
          >
            {qaLabel}
          </div>
        ) : null}
        {bakeError ? <p className="mb-3 text-sm text-destructive">{bakeError}</p> : null}
        {confirmError ? <p className="mb-3 text-sm text-destructive">{confirmError}</p> : null}
        {baking ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Składam finalne obrazki…</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {slides.map((slide) => (
              <figure key={slide.id || slide.index_n} className="min-w-0">
                <img
                  src={`${api.carouselSlidePng(item.id, slide.index_n)}?v=${bust}`}
                  alt={`slajd ${slide.index_n}`}
                  className={cn(
                    "aspect-[4/5] w-full rounded border bg-muted object-cover",
                    slide.qa_ok === false ? "border-2 border-red-600" : "border-border",
                  )}
                />
                <figcaption className="mt-1 text-[11px] text-muted-foreground">
                  {slide.index_n}. {slide.slide_type || "slajd"}
                  {slide.qa_ok === false && slide.qa_note ? ` · ${slide.qa_note}` : ""}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
        {qaFailed ? (
          <label className="mt-4 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={qaOverride}
              onChange={(e) => onQaOverride(e.target.checked)}
            />
            i tak publikuję
          </label>
        ) : null}
        {mode === "approve" && onWhenChange ? (
          <div className="mt-4 rounded-md border border-border bg-muted/20 px-3 py-2">
            <div className="text-sm font-medium">Termin publikacji{slotLabel ? `: ${slotLabel}` : ""}</div>
            {slotReason ? <p className="mt-1 text-xs text-muted-foreground">{slotReason}</p> : null}
            <Input
              type="datetime-local"
              className="mt-2 max-w-xs"
              value={when || ""}
              onChange={(e) => onWhenChange(e.target.value)}
            />
          </div>
        ) : null}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onFixLayout} disabled={busy}>
            {fixPending ? "Poprawiam…" : "Popraw skład"}
          </Button>
          <Button
            size="sm"
            variant="brand"
            disabled={busy || Boolean(bakeError) || !slides.length || qaBlocked}
            onClick={onConfirm}
          >
            {mode === "publish"
              ? confirmPending
                ? "Wysyłam…"
                : "Wyślij te obrazki"
              : confirmPending
                ? "Zatwierdzam…"
                : when
                  ? "Zatwierdzam i planuję"
                  : "Zatwierdzam te obrazki"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function SourceSection({
  title,
  hint,
  tip,
  checked,
  onChecked,
  count = 0,
  icon: Icon,
  tone = "muted",
  children,
}: {
  title: string;
  hint: string;
  tip?: string;
  checked: boolean;
  onChecked: (value: boolean) => void;
  count?: number;
  icon?: LucideIcon;
  tone?: SettingsTone;
  children: ReactNode;
}) {
  const label = count > 0 ? `${title} · ${count}` : title;
  return (
    <div
      className={cn(
        "rounded-md border border-l-4 p-3",
        TONE_CLASS[tone].bar,
        checked ? "border-border" : "border-border/60 bg-muted/30",
      )}
    >
      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={checked}
          onChange={(e) => onChecked(e.target.checked)}
        />
        {Icon ? <Icon className={cn("mt-0.5 h-4 w-4 shrink-0", TONE_CLASS[tone].icon)} /> : null}
        <span>
          <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
            {label}
            <InfoTip text={tip || hint} />
          </span>
          <span className="block text-xs text-muted-foreground">{hint}</span>
        </span>
      </label>
      <div className={cn("mt-3", !checked && "pointer-events-none opacity-50")}>{children}</div>
    </div>
  );
}

export function SourceHits({
  hits,
  loading,
  empty,
}: {
  hits: {
    type?: string;
    kind?: string;
    id?: string;
    excerpt?: string;
    meta?: string;
    usage_label?: string;
  }[];
  loading: boolean;
  empty: string;
}) {
  if (loading && hits.length === 0) {
    return <p className="text-xs text-muted-foreground">Ładuję źródła…</p>;
  }
  if (hits.length === 0) {
    return <p className="text-xs text-muted-foreground">{empty}</p>;
  }
  return (
    <div className="grid max-h-56 gap-2 overflow-auto sm:grid-cols-2 xl:grid-cols-3">
      {hits.map((hit, i) => (
        <div key={`${hit.type}-${hit.id}-${i}`} className="rounded-md border border-border p-2 text-xs">
          <div className="text-muted-foreground">
            {hit.meta || hit.kind || hit.type}
            {hit.usage_label ? ` · ${hit.usage_label}` : ""}
          </div>
          <p className="mt-0.5 text-foreground">{hit.excerpt}</p>
        </div>
      ))}
    </div>
  );
}

export function InspirationPanel({
  item,
  uploadM,
  analyzeM,
  onAnalyze,
  track,
}: {
  item: CarouselItem;
  uploadM: { mutate: (file: File) => void; isPending: boolean };
  analyzeM: { mutate: (opts?: { regenerate?: boolean; force_fetch?: boolean }) => void; isPending: boolean };
  onAnalyze?: (opts?: { regenerate?: boolean; force_fetch?: boolean }) => void;
  track?: "operator" | "ai" | "";
}) {
  const files = item.inspiration_files || [];
  const analysis = item.inspiration_analysis;
  const needsUpload = (item.inspiration || []).some((row) => row.needsUpload);
  const suggest = (analysis?.borrow_suggest || []).join(", ");
  const canRebuildConcepts = track === "ai" && (item.ideas || []).length > 0;
  return (
    <StudioSection
      role="optional"
      title="Odczyt slajdów (inspiracja)"
      hint="Permalink albo zrzut referencji. Nie czyta Twojej karuzeli."
    >
      {files.length ? (
        <div className="flex flex-wrap gap-2">
          {files.map((name) => (
            <img
              key={name}
              src={api.carouselInspirationFile(item.id, name)}
              alt={name}
              className="h-20 w-16 rounded border border-border object-cover"
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          {needsUpload
            ? "Nie udało się ściągnąć slajdów. Wrzuć zrzuty."
            : "Brak obrazków. Po linku studio spróbuje je ściągnąć przy składaniu koncepcji."}
        </p>
      )}
      {analysis?.seen || analysis?.idea || analysis?.layout_note ? (
        <div className="space-y-1 text-xs text-foreground">
          <p>
            <span className="text-muted-foreground">Widzę: </span>
            {[analysis.layout_note, analysis.hierarchy, analysis.photo_kind, analysis.colors]
              .filter(Boolean)
              .join(" · ") || analysis.idea}
          </p>
          {suggest ? (
            <p>
              <span className="text-muted-foreground">Warto wziąć: </span>
              {suggest}
            </p>
          ) : null}
          {analysis.do_not_copy ? (
            <p>
              <span className="text-muted-foreground">Nie kopiuj: </span>
              {analysis.do_not_copy}
            </p>
          ) : null}
        </div>
      ) : analysis?.error ? (
        <p className="text-xs text-muted-foreground">{analysis.error}</p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center rounded-md border border-input px-2 py-1 text-xs">
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadM.mutate(file);
              e.target.value = "";
            }}
          />
          Wrzuć zrzut
        </label>
        <div className="space-y-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploadM.isPending}
            onClick={() => (onAnalyze || analyzeM.mutate)({ force_fetch: true })}
          >
            {analyzeM.isPending ? "Analiza · w toku" : "Przeanalizuj slajdy"}
          </Button>
          <CtaIo io="inspiration" />
        </div>
        {canRebuildConcepts ? (
          <div className="space-y-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={analyzeM.isPending}
              onClick={() => (onAnalyze || analyzeM.mutate)({ regenerate: true })}
            >
              Przebuduj koncepcje
            </Button>
            <CtaIo io="concepts" />
          </div>
        ) : null}
      </div>
    </StudioSection>
  );
}

export function Chip({
  active,
  onClick,
  children,
  tip,
  tone = "brand",
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  tip?: string;
  tone?: SettingsTone;
}) {
  const btn = (
    <button
      type="button"
      className={cn(
        "rounded-full border px-2.5 py-1 text-xs text-foreground",
        active ? cn(TONE_CLASS[tone].chip, "border-current") : "border-border text-muted-foreground hover:bg-accent/40",
      )}
      onClick={onClick}
    >
      {children}
    </button>
  );
  if (!tip) return btn;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{btn}</TooltipTrigger>
        <TooltipContent>{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ChoiceCard({
  active,
  onClick,
  title,
  meta,
  excerpt,
  tip,
  swatch,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  meta?: string;
  excerpt?: string;
  tip?: string;
  swatch?: { bg?: string; accent?: string; ink?: string };
  icon?: ReactNode;
}) {
  const card = (
    <button
      type="button"
      className={cn(
        "rounded-md border p-2.5 text-left text-xs text-foreground",
        active ? "border-brand bg-brand/10" : "border-border hover:bg-accent/40",
      )}
      onClick={onClick}
    >
      {swatch && (
        <div className="mb-1.5 flex h-7 overflow-hidden rounded border border-border">
          <span className="flex-1" style={{ background: swatch.bg || "#f8f5ef" }} />
          <span className="w-5" style={{ background: swatch.accent || "#b5614e" }} />
          <span className="w-5" style={{ background: swatch.ink || "#281f1c" }} />
        </div>
      )}
      <div className="flex items-start gap-2">
        {icon ? (
          <span
            className={cn(
              "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border",
              active ? "border-brand/40 bg-brand/15 text-brand" : "border-border bg-slate-50 text-slate-600",
            )}
          >
            {icon}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="font-medium leading-snug">{title}</div>
          {meta && <div className="mt-0.5 text-muted-foreground">{meta}</div>}
          {excerpt && <p className="mt-1 leading-snug text-muted-foreground">{excerpt}</p>}
        </div>
      </div>
    </button>
  );
  if (!tip) return card;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{card}</TooltipTrigger>
        <TooltipContent className="max-w-xs">{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function CopyPackButton({
  item,
  outgoing,
}: {
  item?: CarouselItem;
  outgoing?: ConceptsTraceOutgoing | null;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={async () => {
        const ok = await copyText(packFromItem(item, outgoing));
        setCopied(ok);
        window.setTimeout(() => setCopied(false), 2500);
      }}
    >
      <Copy className="mr-2 h-3.5 w-3.5" />
      {copied ? "Skopiowano" : "Kopiuj przebieg"}
    </Button>
  );
}

export function FieldLabel({
  children,
  tip,
  icon: Icon,
  tone = "muted",
}: {
  children: ReactNode;
  tip: string;
  icon?: LucideIcon;
  tone?: SettingsTone;
}) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-foreground">
      {Icon ? <Icon className={cn("h-3.5 w-3.5", TONE_CLASS[tone].icon)} /> : null}
      <span>{children}</span>
      <InfoTip text={tip} />
    </div>
  );
}

export function TipBadge({
  children,
  tip,
  variant,
  icon: Icon,
}: {
  children: ReactNode;
  tip: string;
  variant?: "default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "brand";
  icon?: LucideIcon;
}) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Badge variant={variant} className="gap-1">
              {Icon ? <Icon className="h-3 w-3" /> : null}
              {children}
            </Badge>
          </span>
        </TooltipTrigger>
        <TooltipContent>{tip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function CostFooter({ item }: { item?: CarouselItem }) {
  const usage = item?.usage;
  const events = usage?.events || [];
  if (!events.length) {
    return <p className="text-xs text-muted-foreground">Koszt: n/d</p>;
  }
  const totals = usage?.totals;
  const usd = Number(totals?.usd || 0).toFixed(2);
  const inn = totals?.in || 0;
  const out = (totals?.out || 0) + (totals?.think || 0);
  const images = totals?.images || 0;
  const tip = events.map((e) => `${e.step}: $${Number(e.usd || 0).toFixed(4)} (${e.model})`).join(" · ");
  return (
    <p className="text-xs text-muted-foreground" title={tip}>
      Koszt: ${usd} ({(inn / 1000).toFixed(1)}k in / {(out / 1000).toFixed(1)}k out+think, {images} obrazów)
    </p>
  );
}

export function tabForExisting(row: CarouselItem): StudioTab {
  const active = row.pipeline?.active_id || "";
  const llm = String(row.llm_status || "");
  if (
    row.compose_mode === "operator" &&
    ["layout_plan", "draft_critique", "draft_place", "draft_caption", "review"].includes(llm)
  ) {
    return "draft";
  }
  if (active === "wyniki" || (row as { retro_results?: { id?: string } }).retro_results?.id) {
    if (row.status === "published" && active === "wyniki") return "wyniki";
  }
  if (active === "retro" || llm === "retro") {
    return "retro";
  }
  if (
    active === "publish" ||
    row.status === "published" ||
    row.status === "scheduled" ||
    row.status === "publishing" ||
    row.status === "failed"
  ) {
    return "publish";
  }
  if (active === "images" || row.render_status === "done" || (row.slides || []).length) {
    return "render";
  }
  if (active === "concepts" || active === "copy" || row.status === "draft" || row.status === "approved") {
    if (row.compose_mode === "operator" && (row.layout_plan?.slides?.length || row.concepts_source === "draft")) {
      return "draft";
    }
    return "copy";
  }
  return "brief";
}

export function CarouselQueueList({
  items,
  activeId,
  empty,
  onOpen,
  onNext,
  onRate,
  onRetro,
  onDelete,
  onRetry,
  retryPendingId,
  deletePendingId,
}: {
  items: CarouselItem[];
  activeId?: string | null;
  empty: string;
  onOpen: (row: CarouselItem) => void;
  onNext: (id: string) => void;
  onRate: (id: string, rating: string) => void;
  onRetro: (id: string) => void;
  onDelete: (row: CarouselItem) => void;
  onRetry: (row: CarouselItem) => void;
  retryPendingId?: string;
  deletePendingId?: string;
}) {
  const [q, setQ] = useState("");
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? items.filter((row) => {
        const hay = [row.title, row.hook, row.short_id, row.status, row.pain_id, row.format_id, row.week]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(needle);
      })
    : items;
  return (
    <div className="space-y-2">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="szukaj po tytule albo id…" />
      <div className="max-h-[520px] space-y-2 overflow-auto">
        {filtered.map((row) => (
          <QueueRow
            key={row.id}
            row={row}
            active={row.id === activeId}
            onOpen={() => onOpen(row)}
            onNext={() => onNext(row.id)}
            onRate={(rating) => onRate(row.id, rating)}
            onRetro={() => onRetro(row.id)}
            onDelete={() => onDelete(row)}
            onRetry={() => onRetry(row)}
            retryPending={retryPendingId === row.id}
            deletePending={deletePendingId === row.id}
          />
        ))}
        {!filtered.length ? <p className="text-sm text-muted-foreground">{empty}</p> : null}
      </div>
    </div>
  );
}

export function QueueRow({
  row,
  active,
  onOpen,
  onNext,
  onRate,
  onRetro,
  onDelete,
  onRetry,
  retryPending,
  deletePending,
}: {
  row: CarouselItem;
  active: boolean;
  onOpen: () => void;
  onNext: () => void;
  onRate: (rating: string) => void;
  onRetro: () => void;
  onDelete: () => void;
  onRetry: () => void;
  retryPending?: boolean;
  deletePending?: boolean;
}) {
  const ratings = [
    ["negative", "−"],
    ["neutral", "0"],
    ["positive", "+"],
    ["hero", "★"],
  ] as const;
  return (
    <div
      className={cn(
        "flex items-start justify-between gap-2 rounded-md border p-2 text-sm",
        active ? "border-brand bg-brand/10" : "border-border",
      )}
    >
      <button type="button" className="text-left" onClick={onOpen}>
        <div className="flex items-center gap-2">
          <Images className="h-3.5 w-3.5" />
          <span className="font-medium">{row.title || row.hook || row.short_id}</span>
          {row.kind === "carousel" || !row.kind ? (
            <span className="text-[10px] uppercase text-muted-foreground">karuzela</span>
          ) : null}
          {row.kind === "feed_post" ? <span className="text-[10px] uppercase text-muted-foreground">post</span> : null}
          {row.kind === "czasotekst" ? <span className="text-[10px] uppercase text-muted-foreground">czasotekst</span> : null}
          {row.funnel_stage ? (
            <span className="text-[10px] uppercase text-muted-foreground">{row.funnel_stage}</span>
          ) : null}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          {[row.pain_id, row.book_id, row.tool_id, row.style_id, row.format_id].filter(Boolean).join(" · ") ||
            row.title ||
            row.short_id}{" "}
          · <span title={STATUS_TIPS[row.status] || row.status}>{STATUS_LABEL[row.status] || row.status}</span>
          {row.insights?.save_rate != null ? ` · zapis ${pct(row.insights.save_rate)}` : ""}
          {row.insights?.share_rate != null ? ` · wysłania ${pct(row.insights.share_rate)}` : ""}
          {row.insights?.deep_action_rate != null ? ` · deep ${pct(row.insights.deep_action_rate)}` : ""}
          {row.insights?.score != null ? ` · score ${row.insights.score}` : ""}
          {row.rating ? ` · ${row.rating}` : ""}
        </div>
        {row.publish_error && <div className="mt-1 text-xs text-destructive">{row.publish_error}</div>}
        {row.pipeline?.cost_label ? (
          <div className="mt-1 text-[11px] text-muted-foreground">{row.pipeline.cost_label}</div>
        ) : null}
      </button>
      <div className="flex shrink-0 flex-col items-end gap-1">
        {(row.ig_permalink || row.fb_permalink || row.fb_post_id) && row.status === "published" ? (
          <div className="flex flex-wrap justify-end gap-2 text-xs">
            {row.ig_permalink ? (
              <a href={row.ig_permalink} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                IG
              </a>
            ) : null}
            {row.fb_permalink || row.fb_post_id ? (
              <a
                href={row.fb_permalink || `https://www.facebook.com/${row.fb_post_id}`}
                target="_blank"
                rel="noreferrer"
                className="text-brand hover:underline"
              >
                FB
              </a>
            ) : null}
          </div>
        ) : null}
        <div className="flex items-center gap-1">
          {ratings.map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={cn(
                "rounded border px-1.5 py-0.5 text-[11px]",
                row.rating === value ? "border-brand bg-brand/10" : "border-border text-muted-foreground",
              )}
              onClick={() => onRate(row.rating === value ? "" : value)}
            >
              {label}
            </button>
          ))}
          <Button
            size="sm"
            variant="outline"
            className="h-7 border-destructive/40 px-2 text-destructive hover:bg-destructive/10 hover:text-destructive"
            disabled={deletePending}
            onClick={onDelete}
            title="Usuń z kolejki. Opublikowany post na IG/FB zostaje."
          >
            <Trash2 className="mr-1 h-3.5 w-3.5" />
            Usuń
          </Button>
        </div>
        {row.status === "failed" && (
          <Button size="sm" variant="ghost" disabled={retryPending} onClick={onRetry}>
            Ponów
          </Button>
        )}
        {row.status === "published" && (
          <>
            <Button size="sm" variant="ghost" onClick={onRetro}>
              Retro
            </Button>
            <Button size="sm" variant="ghost" onClick={onNext}>
              Zrób następną na bazie tego
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
